/**
 * Krabbelgotchi, het vierde spelletje in de Arcadehal.
 *
 * In de code heet dit `tam`/`tamagotchi`, en dat blijft zo: de spelregels staan in
 * src/lib/tamagotchi.js en die verwijzing gaat verloren als je de codenaam meesleept.
 * Zie sectie 13 van CLAUDE.md.
 *
 * De spelregels staan in lib/tamagotchi.js; hier staat alles wat met het scherm
 * te maken heeft: het bewaren, de klok, de knoppen en het tekenen.
 *
 * Het beestje leeft door terwijl de app dicht is. Daarom staat er in de opslag
 * niet hoe vol zijn buik is maar wanneer hij at; hoe het ervoor staat wordt
 * uitgerekend op het moment dat je kijkt. Zie de kop van lib/tamagotchi.js.
 */

import { api, asJson } from '../lib/api.js';
import { PB_URL } from '../lib/config.js';
import { PALETTE_HEX } from '../lib/palette.js';
import { avatarUrl } from '../lib/pixels.js';
import { playBeestDeun, playBlip, playFanfare, playMemPaar, playPop, playWormBonus, playWormHap } from '../lib/sound.js';
import * as vecht from '../lib/battle.js';
import * as tam from '../lib/tamagotchi.js';
import * as store from '../lib/store.js';

const VELD = tam.VELD;
/** De bovenste rijen van het schermpje: daar staan de meters, of een naam. */
const VELD_KOP = tam.KOP;
/**
 * Hier wonen de levende beestjes: één regel per persoon, op authorId.
 *
 * De getekende stukjes stonden hier ook, en die zijn verhuisd naar `paal_beestje_ontwerp`.
 * Daarom filtert dit niet meer op een lege `sleutel`: dat veld mag uit deze
 * collectie zodra alle ontwerpen verhuisd zijn, en dan zou zo'n filter de hele
 * zoekopdracht laten struikelen. Zie logic/beestje.js.
 */
const COL = 'paal_beestje';

/** Een tik is een halve seconde: genoeg om te wippen, niets voor niets. */
const TIK_MS = 500;

/**
 * Hoe lang je ziet dat je iets deed.
 *
 * Spelen duurt langer dan de rest: daar stuitert een balletje heen en weer, en
 * met anderhalve seconde zie je daar drie standen van. Vier seconden is zes
 * standen, en dan kaatst hij echt.
 */
const ACTIE_MS = 1500;
const SPEEL_MS = 4000;

export default {
  /** Het beestje uit deze browser, of niets. */
  /**
   * Onder welke naam het beestje in deze browser staat: per account.
   *
   * Het stond onder één vaste sleutel, `gp_beest`, die aan niemand hing. Logde je uit
   * en als iemand anders weer in, dan erfde die het beestje van de vorige: hij zag
   * het, en `tamHaal` vond op de server niets van hem en maakte er een kopie van onder
   * zijn eigen naam. Zonder account blijft het `gp_beest`, want dan is er geen naam om
   * het aan te hangen.
   *
   * De oude `gp_beest` wordt niet overgenomen door wie inlogt, juist om die reden. Wie
   * een account heeft staat op de server, en `tamHaal` haalt hem daar op.
   */
  tamOpslag() {
      const id = this.state.accountId;
      return id ? 'gp_beest_' + id : 'gp_beest';
    },

  /**
   * Alles vergeten wat bij het beestje van de vorige persoon hoorde.
   *
   * Aanroepen zodra je van account wisselt: uitloggen, inloggen, een account maken.
   * Zonder dit bleef `tamRec` wijzen naar de regel van de vorige, en ging de eerste
   * aai als PATCH naar díe regel, met het nieuwe `authorId` erop. Dan was die regel
   * van de nieuwe persoon en was de vorige zijn beestje kwijt.
   */
  tamVergeet() {
      this.tamRec = null;
      this.tamRecVan = '';
      this.tamGekeken = false;
      this.tamWacht = null;
      this.tamZocht = false;
      this.tamVoorlopig = false;
      /**
       * Een ophaalbeurt of verzoek dat nog onderweg is hoort bij de vorige.
       *
       * Die vragen we niet terug, maar ze komen er wel aan. Zonder dit tijdperk zette
       * een late GET van de vorige persoon zijn beestje neer onder de sleutel van de
       * nieuwe, en maakte de eerste aai daarna een kloon van hem op jouw naam. Elk
       * antwoord kijkt na zijn `await` of het tijdperk nog hetzelfde is.
       */
      this.tamTijdperk = (this.tamTijdperk || 0) + 1;
      this.tamHaalt = null;
      this.tamOnderweg = null;
      this.tamGeschreven = {};
      // Anders gaf het eerste beestje van de nieuwe persoon een groeifeestje.
      this.tamFase = undefined;
      clearTimeout(this.tamTimer);
      this.tamTimer = null;
      this.setState({
        tamBeest: null, beestOpen: false, tamMissing: false, tamVeldWeg: '',
        // De buren van de vorige: daar stond de nieuwe persoon zelf tussen.
        tamBuren: [], tamRijen: [], tamBurenOpen: false, tamNoemt: false
      });
    },

  tamLees() {
      let b = null;
      try {
        const ruw = store.get(this.tamOpslag());
        if (ruw) b = JSON.parse(ruw);
      } catch (e) {}
      return tam.beestOk(b) ? b : null;
    },

  /**
   * Bewaren doet twee dingen: hier en op de server.
   *
   * Hier, want dat is er meteen en werkt ook zonder account of zonder net. En op
   * de server, want anders begint het beestje op je telefoon opnieuw te leven en
   * is hij weg zodra je je browser leeggooit.
   */
  tamBewaar(b, velden) {
      try { store.set(this.tamOpslag(), JSON.stringify(b)); } catch (e) {}
      this.tamStuur(b, velden);
    },

  /**
   * Het beestje van de server halen, en de verste van de twee houden.
   *
   * Dit loopt na het openen, niet ervoor: het venster gaat meteen open met het
   * beestje uit deze browser en corrigeert zichzelf zodra de server antwoordt.
   * Wachten op het net zou een leeg venster van een halve seconde geven voor iets
   * dat in negen van de tien gevallen hetzelfde is.
   */
  async tamHaal() {
      const id = this.state.accountId;
      if (!PB_URL || !id) return;
      /**
       * Eén tegelijk.
       *
       * Openen, de tekenaar en de trage klok kunnen allemaal een ophaalbeurt starten,
       * en twee die elkaar kruisen kunnen allebei "er staat niets" horen en allebei een
       * regel aanmaken. Loopt er al een, dan wachten we op die.
       */
      if (this.tamHaalt) return this.tamHaalt;
      const beurt = this.tamHaalEcht(id).finally(() => { if (this.tamHaalt === beurt) this.tamHaalt = null; });
      this.tamHaalt = beurt;
      return beurt;
    },

  async tamHaalEcht(id) {
      const tijdperk = this.tamTijdperk || 0;
      const sleutel = this.tamOpslag();
      // Na elke `await`: is dit nog van wie er nu is? Zie tamVergeet.
      const vervallen = () => tijdperk !== (this.tamTijdperk || 0) || this.state.accountId !== id;
      // Wat er na nu nog geschreven wordt, weet de server op het moment van deze
      // vraag niet. Zie tamGeschreven in tamStuur.
      const sinds = this.tamSchrijfTel || 0;
      try {
        // Nieuwste eerst. Staan er per ongeluk twee regels voor dezelfde persoon,
        // dan is de laatst bijgewerkte de echte; de oude blijft ongemoeid liggen
        // in plaats van dat hij je beestje terugzet in de tijd.
        const res = await api(`${PB_URL}/api/collections/${COL}/records?filter=(authorId='${id}')&perPage=1&sort=-updated`);
        if (vervallen()) return;
        if (!res.ok) { this.setState({ tamMissing: true, tamStatus: res.status }); return; }
        const rij = ((await res.json()).items || [])[0];
        if (vervallen()) return;
        // We hebben gekeken. Vanaf nu mag er een regel aangemaakt worden.
        this.tamGekeken = true;
        this.setState({ tamMissing: false, tamStatus: 0 });
        if (!rij) {
          // Niets op de server: dan is het ei van net toch het echte.
          this.tamRec = null;
          this.tamVoorlopig = false;
          // Aanmaken gaat met alles, dus de velden van de wachtrij doen er niet toe.
          const wacht = this.tamWacht;
          this.tamWacht = null;
          this.tamStuur(wacht ? wacht.b : this.state.tamBeest);
          return;
        }
        this.tamRec = rij.id;
        this.tamRecVan = id;
        const vanServer = {
          geboren: Number(rij.geboren) || 0, gevoed: Number(rij.gevoed) || 0,
          gespeeld: Number(rij.gespeeld) || 0, geschept: Number(rij.geschept) || 0,
          // Het veld staat als tekst op de server, dus 'false' is ook uit.
          licht: rij.licht !== false && rij.licht !== 'false', naam: tam.naamOk(rij.naam),
          soort: tam.soortVanBeest(rij), gevoerdDoor: rij.gevoerdDoor || ''
        };
        // Een voorlopig ei legt het altijd af tegen wat er op de server staat. Anders
        // per klok de nieuwste, en niet één hele kopie: zie samenvoegen in
        // lib/tamagotchi.js, waarom een maaltijd van een collega anders verloren ging.
        const wasVoorlopig = this.tamVoorlopig;
        const houden = wasVoorlopig ? vanServer : tam.samenvoegen(this.state.tamBeest, vanServer);
        this.tamVoorlopig = false;
        // Het voorlopige ei was nooit echt, dus van ei naar je echte beestje is geen
        // groei en geen feestje waard.
        if (wasVoorlopig) this.tamFase = undefined;
        if (!houden) return;

        /**
         * Zijn naam en zijn soort komen van de server, ook als de rest van hier komt.
         *
         * `verste` kijkt naar wanneer er het laatst iets met hem is gebeurd: geboren,
         * gevoed, gespeeld, geschept. Een naam veranderen is geen van die vier. Paste
         * je hem in PocketBase aan, dan bleef die klok dus staan, won de kopie in deze
         * browser, en stond de oude naam er even later weer — en bij de volgende hap
         * werd hij ook nog naar de server teruggeschreven. Precies dat "hij springt
         * telkens terug".
         *
         * Een soort verandert na de geboorte nooit meer, dus die mag altijd van de
         * server komen.
         *
         * Behalve als er nog iets van ons onderweg is: dan is onze naam de nieuwe en
         * de zijne de oude. Dat is het geval vlak na hernoemen, want `tamNoem` zet de
         * naam hier en zet hem daarna op de post.
         */
        /**
         * Wat hier veranderd is en nog niet op de server staat, houden we vast.
         *
         * Naam en lamp komen van de server, behalve als we er zelf net aan zaten en
         * dat nog onderweg is: in de wachtrij, of in het verzoek dat nu loopt. Alleen
         * die velden, zodat een aai die onderweg is niet ook jouw oude naam vasthoudt.
         */
        const hier = this.state.tamBeest;
        /**
         * En wat er geschreven is terwijl deze vraag liep.
         *
         * Vertrok de GET, kwam daarna jouw hernoeming aan, en dan pas het antwoord,
         * dan zat in dat antwoord de oude naam en was er niets meer "onderweg". Dan
         * kwam de oude naam terug, tot de volgende minuut. `tamGeschreven` zegt per
         * veld bij welke schrijfbeurt hij het laatst aankwam.
         */
        const geschreven = Object.keys(this.tamGeschreven || {}).filter((k) => this.tamGeschreven[k] > sinds);
        const lokaal = new Set([].concat(
          this.tamWacht ? (this.tamWacht.velden || tam.BEEST_VELDEN) : [],
          this.tamOnderweg || [], geschreven));
        const echt = Object.assign({}, houden,
          hier && lokaal.has('naam') ? { naam: hier.naam } : null,
          hier && lokaal.has('licht') ? { licht: hier.licht !== false } : null);

        // Neerzetten wat we nu voor waar houden, hier en in deze browser.
        try { store.set(sleutel, JSON.stringify(echt)); } catch (e) {}
        this.setState({ tamBeest: echt }, this.tamTeken);

        /**
         * Wat wij weten en de server nog niet: alleen die velden gaan erheen.
         *
         * Naam, soort en geboorte horen daar niet bij, want die komen juist van de
         * server; behalve een hernoeming die nog onderweg is, en die zit in de
         * wachtrij. Staat er iets te wachten, dan gaat het mee met het samengevoegde
         * beestje, zodat het niet met een oude kopie over de server heen schrijft.
         */
        const mis = tam.verschil(echt, vanServer).filter((k) => k !== 'naam' && k !== 'soort' && k !== 'geboren');
        const wacht = this.tamWacht;
        this.tamWacht = null;
        const velden = wacht && !wacht.velden ? null
          : Array.from(new Set([].concat(mis, wacht ? wacht.velden : [])));
        if (wacht || mis.length) this.tamStuur(echt, velden);
      } catch (e) {
        if (!vervallen()) this.setState({ tamMissing: true, tamStatus: 0 });
      }
    },

  /**
   * Het beestje naar de server.
   *
   * Eén regel per persoon, op authorId. Loopt er al een verzoek, dan wordt dit de
   * eerstvolgende: twee gelijktijdige POSTs zouden twee regels voor hetzelfde
   * beestje maken. Dezelfde voorzorg als saveProject in logic/projects.js.
   */
  async tamStuur(b, velden) {
      const id = this.state.accountId;
      if (!PB_URL || !id || !tam.beestOk(b)) return;
      /**
       * Een wachtrij die de velden optelt.
       *
       * Staat er al iets te wachten, dan wordt dit het nieuwe beestje en komen zijn
       * velden bij die van het vorige: aai je en voer je snel na elkaar, dan moeten
       * allebei mee. `null` betekent "alles", en dat blijft alles.
       */
      const wachtrij = (bb, vv) => {
        const oud = this.tamWacht;
        const alles = !vv || (oud && !oud.velden);
        this.tamWacht = { b: bb, velden: alles ? null : Array.from(new Set([].concat(oud ? oud.velden : [], vv))) };
      };
      /**
       * Niets aanmaken voor we hebben gekeken of er al iets staat.
       *
       * Het venster bewaart meteen bij het opengaan (het deuntje telt als een aai),
       * en op een verse pagina is `tamRec` dan nog leeg omdat tamHaal nog onderweg
       * is. Zonder deze rem werd dat een POST, en had je er een regel bij. Elke keer
       * dat je het venster opende. Zo blijft het wachten tot tamHaal antwoord heeft.
       */
      /**
       * Nooit in de regel van een ander schrijven.
       *
       * `tamRec` is de regel van wie er was toen hij gevonden werd. Is dat iemand anders
       * dan wie er nu is (je wisselde van account terwijl er iets onderweg was), dan
       * vergeten we hem en kijken we opnieuw. `tamVergeet` doet dat al bij elke wissel;
       * dit is het net eronder.
       */
      if (this.tamRec && this.tamRecVan && this.tamRecVan !== id) {
        this.tamRec = null; this.tamRecVan = ''; this.tamGekeken = false;
      }
      if (!this.tamRec && !this.tamGekeken) { wachtrij(b, velden); return; }
      if (this.tamBezig) { wachtrij(b, velden); return; }
      this.tamBezig = true;
      // Welke velden er nu onderweg zijn; tamHaal houdt die vast tot ze aankomen.
      this.tamOnderweg = velden && velden.length ? velden.slice() : tam.BEEST_VELDEN.slice();
      /**
       * Bijwerken met alleen de velden die deze handeling raakte.
       *
       * Hier ging altijd de hele regel mee, met wat deze browser wist. Voerde een
       * collega je beestje terwijl jouw venster openstond en aaide jij hem daarna,
       * dan schreef jouw aai zijn oude `gevoed` terug en was de maaltijd van je
       * collega weg. Een aai stuurt nu alleen `gespeeld`, en dan raakt hij die hap
       * niet. Aanmaken gaat wel altijd met alles, want dan is er nog niets.
       */
      const data = tam.naarServer(b, velden, id, !!this.tamRec);
      const tijdperk = this.tamTijdperk || 0;
      const vervallen = () => tijdperk !== (this.tamTijdperk || 0) || this.state.accountId !== id;
      /**
       * Een aanmaakpoging die niet lukte: eerst weer kijken.
       *
       * Brak de POST af terwijl de server hem wel verwerkte, dan stond er al een regel
       * en maakte de volgende aai er een tweede bij. Zo gaat de volgende poging via
       * tamHaal, die de regel vindt als hij er is.
       */
      const mislukt = () => {
        if (this.tamRec) return;
        this.tamGekeken = false;
        wachtrij(b, velden);
      };
      try {
        const res = this.tamRec
          ? await api(`${PB_URL}/api/collections/${COL}/records/${this.tamRec}`, asJson('PATCH', data))
          : await api(`${PB_URL}/api/collections/${COL}/records`, asJson('POST', data));
        /**
         * Een regel die er niet meer is: eerst kijken, dan pas maken.
         *
         * Meteen een POST doen was fout. Een 404 zegt niet alleen "die regel is
         * weg", hij komt er ook uit als de collectie ontbreekt of als de regels
         * hem voor je verbergen. In die twee gevallen maakte elke poging een
         * nieuwe regel, en dan lopen er ineens drie beestjes van dezelfde persoon
         * rond. tamHaal maakt er hooguit één, en alleen als er echt niets staat.
         *
         * Eén poging, en niet meer. Zoeken kan namelijk dezelfde regel opleveren
         * die net 404 gaf, en dan schrijf-404-zoek-schrijf je jezelf de vernieling
         * in: vijftig verzoeken per seconde naar een server die nee zegt.
         */
        if (res.status === 404 && this.tamRec && !this.tamZocht) {
          this.tamZocht = true;
          this.tamRec = null;
          // En we weten weer niet wat er op de server staat, dus dezelfde rem als bij
          // het opstarten: niets aanmaken tot tamHaal gekeken heeft. Zonder dit
          // schoot de wachtrij hieronder er meteen een POST doorheen, want tamRec
          // was net leeggemaakt.
          this.tamGekeken = false;
          this.tamBezig = false;
          // Wat er onderweg was, moet er nog steeds heen. Zonder dit was een
          // hernoeming of een lampklik na een 404 stil weg.
          wachtrij(b, velden);
          return this.tamHaal();
        }
        if (vervallen()) return;
        if (!res.ok) { mislukt(); this.setState({ tamMissing: true, tamStatus: res.status }); return; }
        const rec = await res.json();
        if (vervallen()) return;
        if (rec && rec.id) { this.tamRec = rec.id; this.tamRecVan = id; }
        this.tamSchrijfTel = (this.tamSchrijfTel || 0) + 1;
        this.tamGeschreven = this.tamGeschreven || {};
        Object.keys(data).forEach((k) => { this.tamGeschreven[k] = this.tamSchrijfTel; });
        /**
         * Terugkijken of de server alles bewaard heeft wat we stuurden.
         *
         * PocketBase gooit een veld dat niet bestaat zonder klagen weg. Op 4 september
         * 2026 bleek `gevoed` te ontbreken: elke keer dat je hem te eten gaf verdween
         * dat, dus stond zijn buik voor eeuwig op 1970 en had hij altijd honger. Dat
         * moet je kunnen zien in plaats van erachter komen.
         */
        const kwijt = rec ? Object.keys(data).filter((k) => !(k in rec)) : [];
        this.setState({ tamVeldWeg: kwijt.join(', ') });
        this.tamZocht = false;
        this.setState({ tamMissing: false, tamStatus: 0 });
      } catch (e) {
        if (vervallen()) return;
        mislukt();
        this.setState({ tamMissing: true, tamStatus: 0 });
      } finally {
        this.tamBezig = false;
        this.tamOnderweg = null;
        const volgende = this.tamWacht;
        if (volgende) { this.tamWacht = null; this.tamStuur(volgende.b, volgende.velden); }
      }
    },

  /** De letter waar je op staat een slag draaien. */
  tamDraai(richting) {
      const i = this.state.tamLetterAan || 0;
      const letters = (this.state.tamLetters || ['A', 'A', 'A']).slice();
      letters[i] = tam.draaiLetter(letters[i], richting);
      this.setState({ tamLetters: letters });
      playBlip();
    },

  /** Naar de vorige of volgende letter, en het loopt rond. */
  tamSchuif(richting) {
      const n = tam.NAAM_MAX;
      this.setState({ tamLetterAan: (((this.state.tamLetterAan || 0) + richting) % n + n) % n });
      playBlip();
    },

  /**
   * Ben je nu een naam aan het kiezen?
   *
   * Twee dingen maken dat waar: je hebt op zijn naam geklikt, of hij is uit het ei
   * en heeft er nog geen. Als methode en niet twee keer uitgeschreven, want zowel
   * het doek als de vals hangen eraan en die mogen niet uit elkaar lopen.
   */
  tamNoemtNu() {
      const beest = this.state.tamBeest;
      /**
       * Op bezoek bij de buren wijkt de naamkiezer.
       *
       * Een naamloos beestje vraagt vanzelf om een naam, en dat is goed, maar niet
       * terwijl je bij iemand anders op het schermpje staat te kijken: dan vochten de
       * drie letters en het beestje van de buurman om dezelfde vierentwintig vakjes.
       * Zodra je terugkomt vraagt hij het weer.
       */
      if (this.state.tamBurenOpen || this.state.tamMode === 'arena') return false;
      if (this.state.tamNoemt) return true;
      if (!beest) return false;
      const stand = tam.standVan(beest, Date.now(), new Date().getHours());
      return stand.fase !== 'ei' && !tam.naamOk(beest.naam);
    },

  /** De naam vastleggen zoals de drie letters hem nu spellen. */
  tamNoem() {
      const naam = tam.naamVanLetters(this.state.tamLetters);
      if (!naam) return;
      const beest = this.state.tamBeest;
      if (!naam || !beest) return;
      const na = Object.assign({}, beest, { naam });
      this.tamBewaar(na, ['naam']);
      this.setState({ tamBeest: na, tamNoemt: false });
      playPop();
    },

  /** Van naam veranderen mag altijd; klik op zijn naam. */
  tamHernoem() {
      const beest = this.state.tamBeest;
      this.setState({
        tamNoemt: true, tamLetterAan: 0,
        tamLetters: tam.lettersVanNaam(beest && beest.naam)
      });
    },

  /**
   * Is hij een fase verder dan de vorige keer dat we keken?
   *
   * Dat is een moment: confetti, een deuntje en een melding. Zonder dat gaat hij
   * stilletjes van ei naar baby naar groot en merk je het pas als je toevallig
   * kijkt. De vorige fase staat op de instantie en niet in de toestand: hij hoeft
   * geen hertekening te veroorzaken en hij hoeft een herlaadronde niet te
   * overleven.
   */
  tamKijkNaarGroei(beest) {
      if (!beest) return;
      const nu = tam.standVan(beest, Date.now(), new Date().getHours()).fase;
      const vorige = this.tamFase;
      this.tamFase = nu;
      if (!vorige || !tam.groeide(vorige, nu)) return;
      const naam = tam.noem(beest);
      this.notify(naam + ' is een ' + nu + ' geworden!', avatarUrl(this.state.avatar));
      this.fireConfetti();
      this.spelGeluid(playFanfare);
    },

  // ---- het programmaatje verslepen ---------------------------------------

  /**
   * Oppakken aan de titelbalk en neerzetten waar je wil.
   *
   * Dezelfde greep als bij de Krabbellijn, tot en met de regel dat een klik op een
   * knop in die balk geen sleep is: setPointerCapture legt anders alle verdere
   * pointergebeurtenissen bij de balk neer en dan krijgt het kruisje nooit zijn
   * click. Zie vensterSleepAan in logic/venster.js.
   */

  // ---- de beestjes van de anderen ----------------------------------------

  /**
   * Alle beestjes ophalen, zodat je kunt zien wie honger heeft.
   *
   * Alleen de velden die het lijstje nodig heeft. Het gaat om vierentwintig
   * regels van een handvol getallen, dus dat is klein; toch niet elke tik, maar
   * als je het lijstje opendoet.
   */
  async tamHaalBuren() {
      if (!PB_URL || !this.state.accountId) return;
      this.setState({ tamBurenBezig: true });
      const ok = await this.tamHaalRijen();
      this.setState({ tamBurenBezig: false });
      if (!ok) this.setState({ tamMissing: true });
    },

  /**
   * Alle beestjes van de paal, in één keer.
   *
   * Twee dingen leven hiervan: het lijstje waarmee je langs de buren bladert
   * (`tamBuren`, zonder jouw eigen beestje) en de meldingen bij de bel over wie wie
   * te eten heeft gegeven (`tamRijen`, met jouw eigen beestje erbij, want juist dat
   * wil je horen). Eén verzoek voor beide: het waren er twee van precies dezelfde
   * regels, en dan is de tweede zonde.
   *
   * Alleen de velden die die twee nodig hebben. Het gaat om een regel per collega
   * met een handvol getallen erin.
   */
  async tamHaalRijen() {
      const id = this.state.accountId;
      if (!PB_URL || !id) return false;
      try {
        const res = await api(`${PB_URL}/api/collections/${COL}/records?perPage=200`
          + '&fields=id,authorId,naam,geboren,gevoed,gespeeld,geschept,gevoerdDoor,soort,licht,updated');
        if (!res.ok) { this.setState({ tamStatus: res.status }); return false; }
        const alle = ((await res.json()).items || []).filter((r) => r.authorId);
        this.setState({ tamRijen: alle, tamBuren: alle.filter((r) => r.authorId !== id) });
        return true;
      } catch (e) {
        this.setState({ tamStatus: 0 });
        return false;
      }
    },

  openTamBuren() {
    return this.setState({ tamBurenOpen: true, tamBuurAan: 0, tamFout: '' }, () => {
      this.tamHaalBuren();
      this.tamTeken();
    });
  },

  sluitTamBuren() { return this.setState({ tamBurenOpen: false }, this.tamTeken); },

  /** Het beestje van een collega, uit zijn regel op de server. */
  buurBeest(rij) {
    if (!rij) return null;
    return {
      geboren: Number(rij.geboren) || 0, gevoed: Number(rij.gevoed) || 0,
      gespeeld: Number(rij.gespeeld) || 0, geschept: Number(rij.geschept) || 0,
      licht: rij.licht !== false && rij.licht !== 'false',
      naam: tam.naamOk(rij.naam), soort: tam.soortVanBeest(rij),
      // Van wie hij is, want elk beestje heeft zijn eigen tekening. Zonder dit tekende
      // de burencarrousel elke buurman met de standaard van zijn soort.
      authorId: rij.authorId || ''
    };
  },

  /** Bij wie je nu op bezoek bent. */
  buurNu() {
    const rijen = this.state.tamBuren || [];
    if (!rijen.length) return null;
    const n = ((this.state.tamBuurAan || 0) % rijen.length + rijen.length) % rijen.length;
    return rijen[n];
  },

  /**
   * Een beestje verder of terug.
   *
   * Rond, want een lijstje met een begin en een eind vraagt om een teller op het
   * schermpje, en daar is geen plek. Zo kun je gewoon doorklikken.
   */
  tamBuurStap(richting) {
    const rijen = this.state.tamBuren || [];
    if (rijen.length < 2) return;
    const n = (this.state.tamBuurAan || 0) + (richting < 0 ? -1 : 1);
    this.setState({ tamBuurAan: ((n % rijen.length) + rijen.length) % rijen.length }, this.tamTeken);
    playBlip();
  },

  /** Het beestje waar je nu naar kijkt een appeltje geven. */
  tamVoerBuurNu() {
    const rij = this.buurNu();
    if (rij) this.tamVoerBuur(rij.id);
  },

  /**
   * Het beestje van een collega te eten geven.
   *
   * Dit schrijft in de regel van iemand anders, en dat is hier de bedoeling: ben
   * jij een dag weg, dan kan een ander jouw beestje redden. Er gaat maar één veld
   * omhoog, `gevoed`, plus wie het deed. De rest van zijn regel blijft van hem.
   *
   * Alleen voeren, en niet spelen of opruimen. Voeren is het enige waar iemand
   * anders echt mee helpt; de rest is jouw beestje aaien en dat hoort van jou te
   * blijven.
   */
  async tamVoerBuur(rijId) {
      const ik = this.state.accountId;
      const rij = (this.state.tamBuren || []).find((r) => r.id === rijId);
      if (!PB_URL || !ik || !rij || this.state.tamVoertBuur) return;
      this.setState({ tamVoertBuur: rijId, tamFout: '' });
      try {
        const nu = Date.now();
        const res = await api(`${PB_URL}/api/collections/${COL}/records/${rijId}`,
          asJson('PATCH', { gevoed: nu, gevoerdDoor: ik }));
        if (!res.ok) throw new Error(String(res.status));
        // Ook in `tamRijen`, want daar kijkt de bel naar; anders zag je je eigen hap pas
        // een minuut later in de meldingen van de ander terug.
        const gevoerd = (r) => (r.id === rijId ? Object.assign({}, r, { gevoed: nu, gevoerdDoor: ik }) : r);
        this.setState({
          tamVoertBuur: '',
          tamBuren: (this.state.tamBuren || []).map(gevoerd),
          tamRijen: (this.state.tamRijen || []).map(gevoerd)
        });
        this.spelGeluid(playWormHap);
        // Haakjes om de naam: zonder stond hier `naam || 'Het beestje' + ' heeft
        // gegeten.'`, en omdat `+` voor `||` gaat zei de melding bij een beestje met
        // een naam alleen die naam.
        this.notify((tam.naamOk(rij.naam) || 'Het beestje') + ' heeft gegeten.');
      } catch (e) {
        this.setState({ tamVoertBuur: '', tamFout: 'Voeren mislukt. Probeer het nog eens.' });
      }
    },

  openTamagotchi() {
      // Hier stond de betapoort. De Krabbelgotchi is er nu voor iedereen; alleen de
      // Krabbellijn en de beestjestekenaar zijn nog voor de aangevinkte testers.
      const bestaand = this.tamLees();
      // Welke van de zes je krijgt is een loting, en die gebeurt hier: lib blijft
      // zuiver, want een functie die zelf dobbelt is niet te testen.
      const beest = bestaand || tam.nieuwBeest(Date.now(), 1 + Math.floor(Math.random() * tam.SOORTEN));
      /**
       * Een ei dat we zelf net verzonnen hebben is voorlopig.
       *
       * Staat er niets in deze browser, dan hebben we nog geen idee of er op de
       * server een beestje van je staat: dat antwoord komt een tel later. Zonder dit
       * merkje won dat verse ei van je echte beestje, want `verste` kijkt naar wanneer
       * er het laatst iets gebeurde en bij een nieuw ei is dat nú. En omdat het daarna
       * werd weggeschreven, was je beestje echt weg. Vandaar: tamHaal mag een
       * voorlopig ei altijd overschrijven met wat er op de server staat.
       */
      this.tamVoorlopig = !bestaand;
      /**
       * Het deuntje speelt bij het opstarten, en dat telt als één aai.
       *
       * De knop ervoor is weg: muziek draaien was een van de acht dingen die je kon
       * doen en dat maakte er een handeling van. Zo hoort het bij het apparaatje:
       * je zet hem aan, hij speelt zijn wijsje, en hij is er even blij mee.
       */
      /**
       * Maar één keer per keer dat je hem aanzet, en niet als hij slaapt.
       *
       * Elke klik op de tegel of op een melding liep hierlangs, ook met het venster al
       * open: vier keer klikken en de meter was vol, zonder wachttijd, en een slapend
       * beestje of een ei kreeg zo toch wat tamDoe ze juist weigert.
       */
      const stand = tam.standVan(beest, Date.now(), new Date().getHours());
      const aai = !this.state.beestOpen && stand.fase !== 'ei' && !stand.slaapt;
      const na = aai ? tam.aaien(beest, Date.now()) : beest;
      // Het deuntje is een aai, dus alleen `gespeeld`.
      if (na !== beest) this.tamBewaar(na, ['gespeeld']);
      this.vensterNaarVoren('beest');
      this.spelGeluid(playBeestDeun);

      this.setState({
        beestOpen: true, vensterMin: Object.assign({}, this.state.vensterMin, { beest: false }),
        notifOpen: false, startOpen: false, tamBeest: na, tamTik: 0,
        // De cursor begint bij de eerste letter, en je komt niet binnen in het
        // hernoemscherm omdat je dat de vorige keer openliet.
        tamLetterAan: 0, tamNoemt: false, tamBurenOpen: false,
        // Vanaf nu staat hij in de taakbalk, tot je hem met het kruisje dichtdoet;
        // zie vensterLijst in logic/venster.js.
      }, () => {
        // Eerst kijken wat er op de server staat. Is dat niets, dan zet tamHaal
        // dit beestje daar neer; is het verder, dan neemt hij het over. Alleen een
        // vers ei zetten we hier al vast, anders staat een nieuw beestje pas na
        // het antwoord van de server in deze browser.
        if (!bestaand) { try { store.set(this.tamOpslag(), JSON.stringify(na)); } catch (e) {} }
        this.tamHaal();
        this.haalBeestjes();
      });
      this.tamKlok();
      setTimeout(() => this.tamTeken(), 0);
    },

  closeTamagotchi() {
      clearTimeout(this.tamTimer);
      this.tamTimer = null;
      this.setState({ beestOpen: false, vensterMin: Object.assign({}, this.state.vensterMin, { beest: false }) });
    },

  tamKlok() {
      clearTimeout(this.tamTimer);
      // Ook stil als hij weggeklapt is. Deze klok tikt twee keer per seconde en elke
      // tik hertekent de hele app, en een venster dat je niet ziet hoeft niet te
      // wippen. Het beestje leeft intussen gewoon door: zijn stand wordt uit de
      // tijdstippen uitgerekend, niet uit de tikken. `vensterTerug` zet hem weer aan.
      if (!this.state.beestOpen || (this.state.vensterMin || {}).beest) return;
      this.tamTimer = setTimeout(() => {
        // Op de achtergrond hoeft er niets te wippen.
        if (typeof document === 'undefined' || !document.hidden) {
          this.setState({ tamTik: (this.state.tamTik || 0) + 1 });
          this.tamKijkNaarGroei(this.state.tamBeest);
          this.tamTeken();
        }
        this.tamKlok();
      }, TIK_MS);
    },

  /**
   * Eén van de zes dingen die je voor hem kunt doen.
   *
   * De lamp mag altijd, ook als hij slaapt: daarmee maak je hem juist wakker. De
   * weegschaal mag ook altijd, want die verandert niets. De rest niet: een ei kun
   * je niets aanbieden en een slapend beestje laat je slapen.
   */
  tamDoe(wat) {
      const beest = this.state.tamBeest;
      if (!beest) return;
      const nu = Date.now();
      const stand = tam.standVan(beest, nu, new Date().getHours());
      const altijd = wat === 'lamp' || wat === 'wegen';
      if (!altijd && (stand.fase === 'ei' || stand.slaapt)) return;

      // Zelf voeren zet jouw naam eronder. `tam.voeren` raakt alleen `gevoed` aan, en
      // dan bleef de naam staan van de collega die hem daarvoor voerde, met de tijd
      // van jouw hap: de melding "Reintje gaf Bat te eten" sprong bij elke eigen
      // maaltijd weer als vers nieuws bovenaan de bel.
      const na = wat === 'voeren' ? Object.assign(tam.voeren(beest, nu), { gevoerdDoor: this.state.accountId || '' })
        : wat === 'spelen' ? tam.spelen(beest, nu)
          : wat === 'aaien' ? tam.aaien(beest, nu)
            : wat === 'lamp' ? tam.lamp(beest)
              : wat === 'scheppen' ? tam.scheppen(beest, nu)
                : beest;
      // Alleen het veld dat deze handeling raakte; zie tamStuur.
      const VELD = { voeren: ['gevoed', 'gevoerdDoor'], spelen: ['gespeeld'], aaien: ['gespeeld'],
        lamp: ['licht'], scheppen: ['geschept'] };
      if (na !== beest) this.tamBewaar(na, VELD[wat] || null);
      this.setState({ tamBeest: na, tamActie: { soort: wat, tot: nu + (wat === 'spelen' ? SPEEL_MS : ACTIE_MS) } });
      this.spelGeluid(wat === 'voeren' || wat === 'aaien' ? playWormHap
        : wat === 'spelen' ? playMemPaar : playWormBonus);
      this.tamTeken();
    },

  /** Het beestje op het doek: één canvas-pixel per vakje. */
  tamTeken() {
      const cv = this.tamRef && this.tamRef.current;
      if (!cv) return;
      const ctx = cv.getContext('2d');
      ctx.clearRect(0, 0, VELD, VELD);
      const beest = this.state.tamBeest;
      if (!beest) return;
      const nu = Date.now();
      const stand = tam.standVan(beest, nu, new Date().getHours());
      // De actie gaat mee naar pixels(): daar hoort het appeltje en het hartje,
      // want dat is tekenwerk en geen toestand van het beestje.
      const actie = this.state.tamActie && this.state.tamActie.tot > nu ? this.state.tamActie.soort : null;
      // Sta je een naam te kiezen, dan laat het schermpje de drie letters zien in
      // plaats van het beestje. Zie de naamstand in lib/tamagotchi.js.
      const kiezer = this.tamNoemtNu()
        ? { letters: this.state.tamLetters || ['A', 'A', 'A'], actief: this.state.tamLetterAan || 0 }
        : null;
      // beeldVan stapelt de lagen: getekend waar de mod iets getekend heeft, en
      // anders zoals de code het doet. De naamkiezer gaat langs pixels(), want dat
      // is geen beestje maar een scherm.
      /**
       * Op bezoek bij de buren: hun beestje met zijn naam erboven.
       *
       * De meters vallen weg, want die vijf rijen bovenin zijn nu voor de naam en er
       * is geen plek voor allebei. Hoe het ervoor staat lees je onder het schermpje,
       * en dat is precies wat je wilde weten voor je hem een appeltje geeft.
       */
      const buur = this.state.tamBurenOpen && this.buurBeest(this.buurNu());
      if (buur) {
        const buurStand = tam.standVan(buur, nu, new Date().getHours());
        const geeft = this.state.tamVoertBuur;
        tam.naamPixels(buur.naam, 0).forEach((p) => {
          ctx.fillStyle = PALETTE_HEX[p.k] || '#000';
          ctx.fillRect(p.x, p.y, 1, 1);
        });
        // De tekening van de buurman zelf: elk beestje heeft zijn eigen, dus hier
        // gaat zijn authorId mee en niet alleen zijn soort. Zie stukSleutel in
        // lib/tamagotchi.js.
        tam.beeldVan(buurStand, this.state.tamTik || 0, geeft ? 'voeren' : null,
          this.state.beestjes, tam.soortVanBeest(buur), buur && buur.authorId)
          .filter((p) => p.y >= VELD_KOP)
          .forEach((p) => {
            ctx.fillStyle = PALETTE_HEX[p.k] || '#000';
            ctx.fillRect(p.x, p.y, 1, 1);
          });
        return;
      }
      const strijd = this.state.tamMode === 'arena' && this.state.arenaGevecht && this.state.arenaTegen;
      const teken = kiezer
        ? tam.pixels(stand, this.state.tamTik || 0, actie, kiezer)
        : strijd
          ? vecht.arenaPixels(this.state.arenaGevecht, this.state.arenaTegen, this.state.beestjes, this.state.tamTik || 0, nu,
            // Zijn tekening en niet die van zijn soort: `tegenstanderVan` zet zijn
            // authorId in het beestje, dus die staat hier al klaar.
            this.state.arenaTegen && this.state.arenaTegen.authorId)
          : tam.beeldVan(stand, this.state.tamTik || 0, actie, this.state.beestjes, tam.soortVanBeest(beest), this.state.accountId);
      teken.forEach((p) => {
        ctx.fillStyle = PALETTE_HEX[p.k] || '#000';
        ctx.fillRect(p.x, p.y, 1, 1);
      });
    },

  tamVals() {
      const mob = this.state.isMobile;
      const ink = 'var(--ink)';
      const beest = this.state.tamBeest;
      const stand = beest ? tam.standVan(beest, Date.now(), new Date().getHours()) : null;
      const uit = !stand || stand.fase === 'ei' || stand.slaapt;
      /**
       * Een knop met een tekentje, vierkant.
       *
       * Er stonden woorden op: Voeren, Aaien, Spelen, Opruimen, Licht uit, Wegen.
       * Zes woorden van dertien pixels onder een schermpje van pixels lezen als een
       * formulier, en op een handheld staan er tekentjes op de knoppen.
       *
       * Het woord blijft wel bestaan, als `titel`: dat is wat je ziet als je erop
       * blijft staan en wat een schermlezer voorleest. Een knop met alleen een
       * emoji heeft anders geen naam.
       */
      const knop = (teken, titel, wat, altijd) => ({
        label: teken, titel, onClick: () => this.tamDoe(wat), uit: altijd ? false : uit,
        style: {
          width: toets + 'px', height: toets + 'px', minWidth: '0', boxSizing: 'border-box',
          padding: '0', justifyContent: 'center', borderRadius: '12px',
          // Ongeveer de helft van de toets, net als de tekentjes in de taakbalk.
          fontSize: Math.max(16, Math.round(toets * 0.4)) + 'px', lineHeight: 1
        }
      });
      const actie = this.state.tamActie && this.state.tamActie.tot > Date.now()
        ? this.state.tamActie.soort : null;
      // Hoe je over hem praat: bij naam zodra hij er een heeft.
      const noem = tam.noem(this.state.tamBeest);
      // Sta je nu een naam te kiezen? Dat is zo zodra je erop klikt, en vanzelf
      // zolang hij uit het ei is en nog naamloos.
      const noemt = this.tamNoemtNu();
      const buurRij = this.state.tamBurenOpen ? this.buurNu() : null;

      /**
       * Eén vakje van het schermpje, en hoe breed het scherm daarmee wordt.
       *
       * Hele pixels per vakje, anders wordt het raster onscherp. De rand van vier
       * telt aan beide kanten mee, dus acht erbij.
       *
       * Het knoppenblok hangt aan diezelfde maat: drie toetsen naast elkaar mogen
       * samen niet breder zijn dan het scherm waar ze onder staan. Zonder die band
       * werd een toets een derde van de vensterbreedte, en dat was 110 pixels: een
       * knoppenblok groter dan het beeld.
       */
      const iw = (typeof window !== 'undefined' && window.innerWidth) || 375;
      const ih = (typeof window !== 'undefined' && window.innerHeight) || 700;
      // Dezelfde som als bij Krabbelworm, tot op de pixel: dezelfde optelling van
      // wat er naast het scherm staat, dezelfde hoogtereserve en dezelfde grenzen
      // van zeven en veertien. Beide velden zijn 24 bij 24, dus beide schermpjes
      // komen op exact dezelfde maat uit. Zie snakeVals in logic/snake.js; draai je
      // aan de een, draai dan ook aan de ander.
      const buiten = mob ? (20 + 8 + 24 + 6 + 20 + 8) : (40 + 8 + 48 + 6 + 28 + 8);
      // In de hoogte reserveert de worm 350 voor alles wat er boven en onder zijn
      // scherm staat. Hier is dat meer: onder het schermpje staan twee tekstregels,
      // soms een naamveld, zes toetsen in twee rijen en de knop naar de buren. Met
      // 350 paste dat op een venster van 757 net niet en ging het venster scrollen.
      //
      // Gevolg: op een hoog venster zijn de twee schermpjes exact even groot, want
      // dan raakt de bovengrens van veertien. Op een laag venster levert de
      // Krabbelgotchi als eerste een vakje in. Dat is de juiste kant om het te laten
      // wijken: een spel waarin je moet scrollen om bij de knoppen te komen is stuk.
      /**
       * Tien en niet veertien, want de toetsen zijn de maat.
       *
       * De acht toetsen zijn nooit groter dan achtenvijftig; dat is de knopmaat van
       * deze app (`TOOL_MAX`). Vier daarvan met drie gaten ertussen is 253 pixels, en
       * daarboven blijft het schermpje breder dan het keypad eronder: bij veertien was
       * het 336 tegen 253, en dan hangt er een keypad van driekwart onder een scherm.
       *
       * Tien vakjes maakt het schermpje 248, en dan vullen de toetsen het precies:
       * vier van 56 met drie gaten van 8. De maat van één toets volgt hieronder uit
       * de schermbreedte, dus dit ene getal zet ze allemaal gelijk.
       *
       * Krabbelworm gebruikt dezelfde som met dezelfde grenzen; die staat los, want
       * daar hangt geen keypad onder het scherm. Zie snakeVals in logic/snake.js.
       */
      const vak = Math.max(7, Math.min(10,
        Math.floor((iw - buiten) / VELD), Math.floor((ih - 430) / VELD)));
      const scherm = vak * VELD + 8;
      // Plek, kader, titelbalk en slepen komen uit de vensterlaag. Pas hier, want
      // de breedte van het venster volgt uit de breedte van het schermpje.
      const venster = this.vensterVals('beest', (scherm + 36) + 'px', mob ? '86dvh' : '90dvh',
        { rand: mob ? 12 : 14 });
      const TOETS_GAT = 7;
      const toets = Math.min(58, Math.floor((scherm - 3 * TOETS_GAT) / 4));
      // De naamstand heeft er vijf op een rij, dus die zijn iets smaller.
      const toetsNaam = Math.min(58, Math.floor((scherm - 4 * TOETS_GAT) / 5));
      /**
       * Het keypad spant precies de schermbreedte.
       *
       * De toetsen zijn hele pixels, dus vier ervan plus drie vaste gaten komen zelden
       * exact op de schermbreedte uit. `space-between` verdeelt wat er overblijft over
       * de drie gaten, en dan staan de buitenste toetsen op de rand van het scherm.
       * Bij tien vakjes is dat 248 tegen 4 × 56 = 224, dus gaten van 8.
       */
      const toetsRij = {
        display: 'grid', width: scherm + 'px', justifyContent: 'space-between',
        gap: TOETS_GAT + 'px', minWidth: '0', margin: '0 auto'
      };
      return {
        showTam: this.vensterInfo('beest').open,
        openTamagotchi: this.openTamagotchi,
        closeTamagotchi: this.closeTamagotchi,

        // ---- het programmaatje ----
        // Plek, kader, titelbalk en slepen komen uit de vensterlaag; die is voor
        // alle programmaatjes gelijk. Zie logic/venster.js.
        /**
         * Het venster in één sleutel in plaats van acht losse.
         *
         * Er stonden hier `tamStaStyle`, `tamVensterStyle`, `tamBalkStyle`,
         * `tamBalkTitelStyle`, `tamNaarVoren` en drie sleepsleutels: precies de acht
         * dingen die de gedeelde vensterschil zelf al uitpakt. Zie views/Venster.jsx.
         * Daarmee krijgt de Krabbelgotchi ook het wegklapstreepje dat elk ander
         * venster heeft, want die knop zit in die schil.
         */
        tamVenster: venster,
        tamRef: this.tamRef,
        tamVeld: VELD,
        // Alleen als er iets aan de hand is, en dan zegt hij wat. Verder houdt dit
        // venster zijn mond; zie sectie 13 van CLAUDE.md.
        // Een ontbrekend veld is iets voor de mod: alleen die kan het maken.
        showTamVeldWeg: !!this.state.tamVeldWeg && !!this.isMaintainer,
        tamVeldWegTekst: 'Op de server ontbreekt het veld ' + (this.state.tamVeldWeg || '')
          + ' in de collectie ' + COL + '. Alles wat daarin hoort te staan wordt weggegooid.',
        showTamMissing: !!this.state.tamMissing && !!this.state.accountId,
        tamMissingTekst: this.state.tamStatus === 403
          ? 'Je beestje blijft in deze browser: de collectie ' + COL + ' mag nog niet gelezen worden.'
          : this.state.tamStatus === 404
            ? 'Je beestje blijft in deze browser. Maak op de server de collectie ' + COL + ' aan met authorId, geboren, gevoed, gespeeld, geschept, licht, naam, soort en gevoerdDoor.'
            : 'Je beestje blijft even in deze browser: de server antwoordde niet.',
        tamMissingStyle: { fontSize: '12px', fontWeight: 600, color: 'var(--fout)', lineHeight: 1.4, textAlign: 'center' },
        tamTitel: 'Minigame: Krabbelgotchi!',
        tamTitelStyle: {
          fontSize: mob ? '17px' : '19px', fontWeight: 700, letterSpacing: '-.01em', flex: '1 1 auto'
        },
        // Het venster is de behuizing. Er stond een wit venster met daarin een lila
        // plaat, elk met een eigen rand van drie én een eigen schaduw van acht: een
        // scherm in een scherm. Dat is bij Krabbelworm, Memory en de Krabbellijn al
        // rechtgezet en het beestje bleef achter, want dat hing toen in de
        // wachtkamer. Nu net als daar: de lila plaat ís het venster, dus één rand en
        // één schaduw. Zie snakePanelStyle en memPanelStyle.
        //
        // Het witte schermpje binnenin blijft wel: dat is het beeld en geen tweede
        // kader.
        tamPanelStyle: {
          flex: '1 1 auto', minHeight: '0',
          overflowY: 'auto', overflowX: 'hidden', overscrollBehavior: 'contain',
          padding: mob ? '12px' : '14px', display: 'flex', flexDirection: 'column', gap: '12px', boxSizing: 'border-box'
        },
        tamConsoleStyle: {
          flex: '0 0 auto', display: 'flex', flexDirection: 'column', gap: '12px',
          minWidth: '0', boxSizing: 'border-box'
        },
        tamBoardStyle: (() => {
          return {
            position: 'relative', flex: '0 0 auto', alignSelf: 'center', boxSizing: 'border-box',
            width: scherm + 'px', height: scherm + 'px',
            border: '4px solid ' + ink, borderRadius: '8px',
            backgroundColor: '#fff',
            backgroundImage: 'linear-gradient(to right, rgba(36,27,58,.06) 1px, transparent 1px),linear-gradient(to bottom, rgba(36,27,58,.06) 1px, transparent 1px)',
            backgroundSize: vak + 'px ' + vak + 'px',
            overflow: 'hidden'
          };
        })(),
        tamCanvasStyle: {
          position: 'absolute', inset: '0', width: '100%', height: '100%',
          imageRendering: 'pixelated', display: 'block'
        },
        // De twee tekstregels onder het schermpje: wat hij doet en hoe oud hij is.
        // Buik en humeur stonden hier ook, als balkjes met een kopje ernaast van
        // tien pixels; die staan nu als pixels op het schermpje zelf.
        tamTekstStyle: { flex: '0 0 auto', display: 'flex', flexDirection: 'column', gap: '3px', minWidth: '0' },
        /**
         * Wat het beestje doet, in één regel. Dit is de enige tekst in het venster
         * en hij vertelt iets wat je niet ziet: waarom hij kijkt zoals hij kijkt.
         */
        tamHumeur: !stand ? ''
          : actie === 'wegen' ? noem + ' weegt ' + tam.gewicht(beest, Date.now()) + ' gram.'
            : stand.fase === 'ei' ? 'Er zit iets in het ei…'
              : stand.slaapt ? (beest && beest.licht === false ? noem + ' doet een tukje. Licht weer aan?' : noem + ' slaapt. Kom morgen terug.')
                : stand.humeur === 'ziek' ? noem + ' is ziek van de honger en de troep.'
                  : stand.humeur === 'hongerig' ? noem + ' heeft honger.'
                    : stand.humeur === 'verdrietig' ? noem + ' verveelt zich.'
                      : noem + ' heeft het prima.',
        // ---- zijn naam ----
        // De vraag komt pas als hij uit het ei is: een ei noem je nog niet.
        showTamNaamVeld: noemt,
        // Terwijl je een naam kiest zijn de wieltjes het knoppenblok. Zo blijft het
        // venster even hoog en hoef je niet te scrollen om bij de knop te komen; en
        // het klopt ook: je bent op dat moment niet aan het voeren.
        showTamKnoppen: !noemt && this.state.tamMode !== 'arena',
        // In de arena zwijgt het venster over hoe het met hem gaat: daar gaat het
        // om wie er tegenover staat.
        /**
         * Deze regels gaan over jóuw beestje.
         *
         * Sta je bij de buren te kijken, dan hoort er niet "hij heeft het prima" onder
         * het schermpje van iemand anders. Dan is `tamBuurRegel` de enige regel, en die
         * gaat wel over wie je ziet.
         */
        showTamTekst: !this.state.tamBurenOpen && !noemt && this.state.tamMode !== 'arena',
        /**
         * De naam kiezen gebeurt op het schermpje zelf, in pixels: drie letters
         * met een knipperend streepje onder degene die aan de beurt is. Hier
         * onder staan alleen de knopjes, zoals op een arcadekast: twee om van
         * letter te wisselen en twee om te draaien.
         *
         * Er stonden hier drie wieltjes in html. Dat werkte, maar het hoort op het
         * scherm: dan is het onderdeel van het apparaatje in plaats van een
         * formulier eronder.
         */
        tamNaamKop: 'Hoe gaat hij heten?',
        tamNaamKopStyle: { fontSize: '12px', fontWeight: 700, textAlign: 'center', color: 'var(--tekst-2)' },
        tamNaamToetsen: [
          { teken: '\u25C0', titel: 'Vorige letter', onClick: () => this.tamSchuif(-1) },
          { teken: '\u25BC', titel: 'Letter terug', onClick: () => this.tamDraai(-1) },
          { teken: '\u25B2', titel: 'Letter verder', onClick: () => this.tamDraai(1) },
          { teken: '\u25B6', titel: 'Volgende letter', onClick: () => this.tamSchuif(1) },
          // Het vinkje hoort bij de pijltjes en niet op een balk eronder: het is de
          // vijfde toets van hetzelfde blok, alleen dan de roze.
          { teken: '\u2713', titel: 'Zo heet hij', onClick: this.tamNoem, klaar: true }
        ].map((t) => Object.assign({}, t, {
          klasse: t.klaar ? 'gp-btn pink' : 'gp-btn',
          style: {
            width: toetsNaam + 'px', height: toetsNaam + 'px', minWidth: '0', boxSizing: 'border-box',
            padding: '0', justifyContent: 'center', borderRadius: '12px',
            fontSize: Math.max(14, Math.round(toetsNaam * (t.klaar ? 0.42 : 0.32))) + 'px', lineHeight: 1
          }
        })),
        tamNoem: this.tamNoem,
        tamHernoem: this.tamHernoem,
        tamNaamKnopStyle: { width: '100%', height: '38px', justifyContent: 'center', borderRadius: '12px', fontSize: '13px' },
        tamNaamRijStyle: { display: 'flex', flexDirection: 'column', gap: '7px', minWidth: '0' },
        tamNaamToetsRijStyle: Object.assign({}, toetsRij, {
          gridTemplateColumns: 'repeat(5, ' + toetsNaam + 'px)'
        }),
        /*
         * Hier stond "Reintje Vos gaf hem te eten", onder de leeftijd.
         *
         * Dat is nieuws en geen toestand: het hoort bij de bel, waar het ook staat
         * als je het venster niet openhebt. In het schermpje bleef het staan tot de
         * volgende keer dat iemand hem voerde, dus na een dag las je nog steeds iets
         * dat gisteren gebeurde. Zie de meldingen in logic/render-vals.js.
         */
        tamHumeurStyle: { fontSize: '13px', fontWeight: 600, textAlign: 'center' },
        /**
         * Een brede witte knop onderaan om hem een naam te geven.
         *
         * Hernoemen kon al, door op de leeftijdregel te klikken, en dat vindt niemand:
         * een grijs regeltje van elf pixels ziet er niet uit als een knop. Nu staat er
         * onder de toetsen waar hij hoort, in het wit van "waar je naartoe gaat" en
         * niet in de roze van een hoofdactie. De leeftijdregel blijft ook werken.
         *
         * Hij is er niet terwijl je al aan het kiezen bent, en niet bij een ei: een ei
         * heeft nog geen naam nodig, dat vraagt hij zelf zodra hij uitkomt.
         */
        showTamNoemKnop: !!stand && stand.fase !== 'ei' && !this.tamNoemtNu(),
        tamNoemKnopLabel: (beest && tam.naamOk(beest.naam)) ? 'Andere naam geven' : 'Geef hem een naam',
        /**
         * De brede knoppen onderin zijn precies zo breed als het schermpje en het
         * keypad. Ze stonden op `width: 100%` en dat is de binnenruimte van het
         * venster: acht pixels breder dan het scherm en negentig breder dan het
         * keypad, dus drie verschillende breedtes onder elkaar.
         */
        tamNoemKnopStyle: {
          width: scherm + 'px', margin: '0 auto', justifyContent: 'center', height: '40px',
          borderRadius: '12px', fontSize: '13px', background: '#fff'
        },
        /**
         * En eronder de weg naar de tekenaar.
         *
         * Zijn uiterlijk is iets van dit beestje, dus die knop hoort hier en niet
         * alleen in het startmenu: je zit naar hem te kijken en wil zijn ogen anders.
         * Hetzelfde witte vlak als de naamknop, want het is dezelfde soort stap.
         */
        showTamUiterlijkKnop: !!stand && stand.fase !== 'ei' && !this.tamNoemtNu(),
        tamUiterlijkLabel: 'Uiterlijk aanpassen',
        openTamUiterlijk: this.openBeestje,
        tamLeeftijdTitel: 'Klik om hem een andere naam te geven',
        tamLeeftijd: !stand ? ''
          : stand.dagen >= 1
            ? Math.floor(stand.dagen) + (Math.floor(stand.dagen) === 1 ? ' dag oud' : ' dagen oud')
            : Math.max(0, Math.floor(stand.minuten)) + ' minuten oud',
        // Een knop die geen knop lijkt: het is een regel tekst waar je op kunt
        // klikken om hem te hernoemen. Vandaar geen rand, geen achtergrond en de
        // cursor van een link.
        tamLeeftijdStyle: {
          fontFamily: 'inherit', fontSize: '11px', fontWeight: 600,
          color: 'var(--tekst-3)', textAlign: 'center',
          border: 'none', background: 'none', padding: '0', cursor: 'pointer', width: '100%'
        },

        // ---- op bezoek bij de beestjes van de anderen ----
        openTamBuren: this.openTamBuren,
        sluitTamBuren: this.sluitTamBuren,
        showTamBuren: !!this.state.tamBurenOpen,
        /**
         * Onder het schermpje: van wie hij is en of hij iets te eten kan gebruiken.
         *
         * De naam van het beestje staat in pixels op het schermpje; hier staat wie
         * zijn baasje is, want dat is een echte naam en die past niet in drie letters.
         * En hoe vol zijn buik is, want dat bepaalt of je die knop indrukt.
         */
        tamBuurRegel: (() => {
          if (!this.state.tamBurenOpen) return '';
          if (this.state.tamBurenBezig) return 'Even kijken…';
          if (!buurRij) return 'Nog geen andere beestjes.';
          const b = this.buurBeest(buurRij);
          const st = tam.standVan(b, Date.now(), new Date().getHours());
          const wie = (this.state.accounts || []).find((a) => a.id === buurRij.authorId);
          const honger = st.honger < 30 ? 'heeft honger' : st.honger < 70 ? 'kan wel wat eten' : 'zit vol';
          return 'van ' + (wie ? wie.name : 'iemand') + ' · ' + honger;
        })(),
        tamBuurRegelStyle: {
          fontSize: '11px', fontWeight: 600, color: 'var(--tekst-2)', textAlign: 'center', lineHeight: 1.4
        },
        showTamFout: !!this.state.tamFout,
        tamFout: this.state.tamFout,
        // Twee rijen van drie, zoals het knoppenblok onder een handheld. Op één rij
        // van zes werden het zes smalle vierkantjes tegen de rand aan; drie naast
        // elkaar geeft grotere toetsen en een blok dat je in één keer overziet.
        // Acht toetsen op vier kolommen. Met zeven op drie kolommen bleef er een gat
        // in de onderste rij, en een keypad met een gat leest als een kapotte knop.
        // De achtste is de weg naar de buren; die stond als losse balk onder het
        // blok en past hier beter, en dat scheelt ook een regel hoogte.
        // Acht toetsen op vier kolommen, dus twee rijen.
        tamKnopRasterStyle: Object.assign({}, toetsRij, {
          gridTemplateColumns: 'repeat(4, ' + toets + 'px)'
        }),
        // Elk tekentje is wat er op het schermpje gebeurt als je erop drukt: een
        // appeltje, een hartje, een balletje. De lamp en de weegschaal staan altijd
        // aan: met de lamp maak je hem juist wakker, en wegen verandert niets.
        /**
         * Op bezoek bij de buren zijn er vier toetsen: heen, terug, eten, klaar.
         *
         * Het was een lijstje met een regel per collega, en dat werd een adresboek
         * naast een apparaatje. Nu blader je erdoor zoals je op zo'n ding hoort te
         * bladeren: pijltje, pijltje, en het beestje staat op het schermpje.
         */
        tamKnoppen: this.state.tamBurenOpen ? [
          Object.assign(knop('\u25C0', 'Vorige', 'buur', true),
            { onClick: () => this.tamBuurStap(-1), uit: (this.state.tamBuren || []).length < 2 }),
          Object.assign(knop('\uD83C\uDF4E', 'Geef een appeltje', 'buur', true),
            { onClick: this.tamVoerBuurNu, uit: !buurRij || !!this.state.tamVoertBuur }),
          Object.assign(knop('\u25B6', 'Volgende', 'buur', true),
            { onClick: () => this.tamBuurStap(1), uit: (this.state.tamBuren || []).length < 2 }),
          Object.assign(knop('\u21A9', 'Terug naar je eigen beestje', 'buur', true),
            { onClick: this.sluitTamBuren })
        ] : [
          // Bestek en geen appeltje: het appeltje staat al op de buikmeter in het
          // schermpje, en op de knop is mes-en-vork "geef hem eten" in plaats van
          // "hier is fruit".
          knop('\uD83C\uDF74', 'Voeren', 'voeren'),
          knop('\u2764\uFE0F', 'Aaien', 'aaien'),
          knop('\u26BD', 'Spelen', 'spelen'),
          knop('\uD83E\uDDF9', 'Opruimen', 'scheppen'),
          knop('\uD83D\uDCA4', beest && beest.licht === false ? 'Licht aan' : 'Licht uit', 'lamp', true),
          knop('\u2696\uFE0F', 'Wegen', 'wegen', true),
          // De laatste twee gaan niet over verzorgen maar over de anderen: bij de
          // buren kijken, en er een uitdagen.
          Object.assign(knop('\uD83D\uDC65', 'Kijk bij de buren', 'buren', true),
            { onClick: this.state.tamBurenOpen ? this.sluitTamBuren : this.openTamBuren }),
          /**
           * De arena staat er wel, maar je kunt er nog niet op klikken.
           *
           * Op verzoek uitgezet: de knop mag er staan, hij mag nog niet werken. De
           * server is er niet de reden — `paal_arena` bestaat en heeft alle twaalf
           * velden die deze code gebruikt, met al een gespeelde partij erin. Er
           * stond hier dat die tafel nog niet bestond; dat was een aanname uit een
           * eerdere sessie en die was fout. Zet je hem weer aan, dan is `uit: true`
           * hieronder het enige dat weg moet.
           *
           * Hij blijft staan en verdwijnt niet, want de rij knoppen is een raster
           * van vier bij twee; er een uit halen laat een gat vallen.
           */
          Object.assign(knop('\u2694\uFE0F', 'Naar de arena \u2014 nog niet open', 'arena', true),
            { onClick: this.openArena, uit: true })
        ]
      };
    }
};
